//@ts-nocheck
import React, { useEffect, useState } from "react";
import useCustomForm from "hooks/useCustomForm";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import Svg from "../../Svg/Svg";
import { v4 as uuid } from "uuid";
import styles from "./UpdateTemplateForm.module.scss";
import reorder from "utils/reorder";
import { useTheme } from "hooks/useSingleton";
import { Button } from "@factorearth/component-library";
import ActionsAndData from "../ActionsAndData/ActionsAndData";
import enforceFieldTemplateDoc from "utils/enforceFieldTemplateDoc";
import ContinuationData from "../ContinuationData/ContinuationData";
import TableFieldsEditor from "../TableFieldsEditor/TableFieldsEditor";
import OptionsEditor from "../OptionsEditor/OptionsEditor";
import enforceOptions from "utils/enforceOptions";
import { Option } from "db/definitions";
import { FiSliders, FiChevronDown, FiChevronUp, FiTerminal, FiTrash2 } from "react-icons/fi";
import { TextFieldWrapper } from "@factorearth/component-library";
import Papa from "papaparse";
import MeasurePixels from "../MeasurePixels/MeasurePixels";

function UpdateTemplateForm(props) {
	const {
		template,
		forceSubmit,
		data,
		submitCallback,
		submitClickCallback,
		handleShowDeleteModal,
		updateProp,
		online,
		userDoc,
		saveForm,
		fromTable,
		docType,
		setupDrawer,
		values,
		handleChange,
		handleSubmit,
		handleUpdateMultiplePropsAtOnce,
	} = props;

	const supportedFieldTypes = [
		"text",
		"note",
		"checkbox",
		"radio",
		"date",
		"number",
		"dropdown",
		"table",
		"image",
		"imageroll",
		"phone",
		"readonlytext",
		"section",
		"geospatial",
		"geospatialutm",
	];
	const supportedCellFieldTypes = [
		"text",
		"note",
		"checkbox",
		"radio",
		"date",
		"number",
		"dropdown",
		"phone",
		"image",
		"readonlytext",
		"embeddedtext",
		"geospatial",
		"geospatialutm",
	];

	const disabledKeys = [
		"id",
		"group",
		"readGroups",
		"createdAt",
		"updatedAt",
		"formTemplate",
		"tableField",
		"fieldTemplateId",
		"fieldTemplates",
		"continuationData",
		"fieldsOrder",
		"optionsOrder",
		"continuationDataId",
		"actionData",
		"publish",
	];

	// This is a list of things that will be hidden under an expandable component in the UI i.e. Advanced Properties
	const advancedProperties = ["type", "exportType", "publish", "actionData", "docId"];

	const devProperties = [
		"id",
		"fieldsOrder",
		"projectId",
		"version",
		"group",
		"readGroups",
		"createdAt",
		"updatedAt",
		"rowOrder",
		"deleted",
		"optionsOrder",
		"exportData",
		"exportAction",
		"versionNumber",
		"formTemplate",
		"tableField",
		"imagesPerPage",
		"exportActions",
		"createdBy",
		"new",
		"pixelFollowing",
		"pixelInitial",
		"rowsAllowed",
		"totalExtraLines",
		"font",
		"fontSize",
		"bold",
		"rollLinkId",
		"continuationDataId",
		"exportInstruction",
		"continuationDataId",
		"continuationData",
		"csvKey",
		"identityRole",
	];

	// Available font options supported by string-pixel-width library
	const fontOptions: FontType[] = [
		"andale mono",
		"arial",
		"avenir",
		"avenir next",
		"comic sans ms",
		"courier new",
		"georgia",
		"helvetica",
		"impact",
		"times new roman",
		"trebuchet ms",
		"verdana",
		"webdings",
		"open sans",
		"tahoma",
	];

	const propByType = {
		action: () => handleActionTypes("action", "actionData"),
		options: (value) => handleOptionsProp(value),
		tableFields: (value) => handleTableFieldsProp(value),
		exportActions: () => handleActionTypes("exportActions", "exportData"),
		continuationDataId: (value) => handleContinuationId(value),
		inputType: (value) => handleInputType(value),
		pixelInitial: (value) => handlePixelMeasureInput(value, "pixelInitial"),
		pixelFollowing: (value) => handlePixelMeasureInput(value, "pixelFollowing"),
		font: (value) => handleFont(fontOptions, "font"),
	};

	const propertyProxies = {
		templateName: "Template Name",
		template: "Default Base Template",
		versionName: "Version Name",
		description: "Description",
		formTemplateGroup: "Template Group",
		exportType: "Template Export Type",
		publish: "Published",
		inputType: "Field Type",
		header: "Field Header",
		csvKey: "CSV Key",
		identityRole: "Identity Role",
	};

	const [advancedOpen, setAdvancedOpen] = useState(false);
	const [developerOpen, setDeveloperOpen] = useState(false);

	const color = useTheme();

	useEffect(() => {
		if (updateProp) {
			if (updateProp.action && updateProp.action === "push") {
				const valueCopy = [...values[updateProp.prop]];
				valueCopy.push(updateProp.data);
				const eventObj = {
					target: {
						name: updateProp.prop,
						value: valueCopy,
					},
				};
				handleChange(eventObj);
			}
		}
	}, [updateProp]);

	useEffect(() => {
		if (forceSubmit) {
			handleSubmit();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [forceSubmit]);

	function toggleDeveloperOpen() {
		setDeveloperOpen(!developerOpen);
	}

	function toggleAdvancedOpen() {
		setAdvancedOpen(!advancedOpen);
	}
	/**
	 * This triggers the handle change so the changes can be tracked in state, but then immediately triggers the handle submit for timing reasons, or if blur doesnt make sense.
	 * @param e event object
	 */
	function handleChangeAndSubmit(e) {
		handleChange(e);
		handleSubmit(e, true);
	}

	/**
	 * handles building the html for the template, will either go to special propByType or default
	 * @returns html
	 */
	function renderTemplate() {
		const final = [];
		const advanced = [];
		const developer = [];
		for (let i = 0; i < Object.keys(values).length; i++) {
			const key = Object.keys(values)[i];
			let html;
			// hiding continuation data, actions and export actions from template manager
			// if (key === "action" || key === "exportActions" || key === "continuationDataId") continue;
			if (propByType[key]) {
				if (key === "options" && values[key] === undefined) {
					continue;
				}
				html = propByType[key](values[key]);
			} else {
				const placeholder = values[key] !== undefined ? String(values[key]) : undefined;
				html = (
					<React.Fragment key={`text-input-wrapper-frag-${key}`}>
						{key === "fieldTemplates" ? null : (
							<div data-test="field-prop-list-item" className={styles.inputWrapper}>
								<label
									style={{ color: disabledKeys.indexOf(key) > -1 ? color.text.secondary : color.text.primary }}
									className={styles.header}
									htmlFor={key}>
									{propertyProxies[key] || key}:
									{key === "exportType" ? (
										<React.Fragment key="export-type-info-fragment-wrapper">
											<div style={{ background: color.background.tertiary, color: color.text.secondary }} className={styles.toolTip_btn}>
												?
											</div>
											<div
												style={{
													background: color.background.primary,
													color: color.text.primary,
													border: `1px solid ${color.border.primary}`,
												}}
												className={styles.toolTip}>
												Your options for export type are: Text, Table, Checkbox.{" "}
											</div>
										</React.Fragment>
									) : null}
								</label>
								<TextFieldWrapper
									id={fromTable ? `${key}-cell` : key}
									type="text"
									value={values[key] || ""}
									name={key}
									disabled={disabledKeys.indexOf(key) > -1}
									onChange={handleChange}
									onBlur={handleSubmit}
									placeholder={placeholder}
									colorPalette={color}
									style={{
										backgroundColor: disabledKeys.indexOf(key) > -1 ? color.background.secondary : color.background.primary,
										border: `${color.border.primary} 1px solid `,
										color: disabledKeys.indexOf(key) > -1 ? color.text.secondary : color.text.primary,
									}}
								/>
							</div>
						)}
					</React.Fragment>
				);
			}

			if (!html) continue;

			// check advanced
			if (advancedProperties.includes(key)) {
				// its an advanced:
				advanced.push(html);
			} else if (devProperties.includes(key)) {
				// its a dev one:
				developer.push(html);
			} else {
				// reggae one
				final.push(html);
			}
		}

		const finalHtml = (
			<div>
				<div className={styles.fields_wrapper_container}>
					<div className={styles.userFieldsFormTemplate} data-test={`user-fields-${docType}${fromTable ? "-cell" : ""}`}>
						{final}
					</div>
					<div
						onClick={toggleAdvancedOpen}
						className={styles.advanced_button_container}
						data-test={`advanced-tab-${docType}${fromTable ? "-cell" : ""}`}
						style={{
							backgroundColor: color.background.secondary,
							border: `${color.border.primary} 1px solid`,
							borderBottom: advancedOpen ? "none" : `${color.border.primary} 1px solid`,
							paddingBottom: advancedOpen && "0px",
							color: color.text.tertiary,
						}}>
						<FiSliders />
						<div>Advanced</div>
						{advancedOpen ? <FiChevronUp /> : <FiChevronDown />}
					</div>
					<div
						className={advancedOpen ? styles.advancedOpen : styles.displayNone}
						data-test={`advanced-tab-parent-${docType}${fromTable ? "-cell" : ""}`}
						style={{
							backgroundColor: color.background.secondary,
							color: color.text.primary,
							padding: "24px",
						}}>
						{advanced}
					</div>
					{advancedOpen && (
						<>
							<div
								onClick={toggleDeveloperOpen}
								className={styles.advanced_button_container}
								data-test={`developer-tab-${docType}${fromTable ? "-cell" : ""}`}
								style={{
									backgroundColor: color.background.secondary,
									border: `${color.border.primary} 1px solid `,
									color: color.text.tertiary,
									borderBottom: developerOpen ? "none" : `${color.border.primary} 1px solid`,
									paddingBottom: developerOpen && "0px",
								}}>
								<FiTerminal />
								<div>Developer</div>
								{developerOpen ? <FiChevronUp /> : <FiChevronDown />}
							</div>
							<div
								className={developerOpen ? styles.developerOpen : styles.displayNone}
								style={{
									backgroundColor: color.background.secondary,
									color: color.text.primary,
									padding: "24px",
								}}
								data-test={`developer-tab-parent-${docType}${fromTable ? "-cell" : ""}`}>
								{developer}
							</div>
						</>
					)}
				</div>
			</div>
		);

		return finalHtml;
	}

	/**
	 * Handle proptype inputType
	 * @param {String} value of the field
	 * @returns html
	 */
	function handleInputType(value) {
		const fieldTypes = fromTable ? supportedCellFieldTypes : supportedFieldTypes;
		const html = handleDropdownInput(value, "inputType", fieldTypes);
		return html;
	}

	/**
	 * Handles all "action types"
	 * @param {string} actionProp name of the property for the actionTypes (action or exportActions)
	 * @param {string} dataProp name of the property for the actionData (actionData or exportData)
	 * @returns html.
	 */
	function handleActionTypes(actionProp, dataProp) {
		const html = (
			<div className={styles.row} data-test="field-prop-list-item" isfancy="true" name={actionProp} key={`action-${actionProp}`}>
				<ActionsAndData
					actionDataList={values[dataProp]}
					actionTypeList={values[actionProp]}
					actionProp={actionProp}
					dataProp={dataProp}
					templateData={values}
					fromTable={fromTable}
					handleChange={handleChangeActionTypes}
					handleShowDeleteModal={handleShowDeleteModal}
					setupDrawer={setupDrawer}
					color={color}
				/>
			</div>
		);
		return html;
	}

	/**
	 * This handles changes for when both the action and action data properties have to be updated at the same time.  There are issues with doing them one at a time with state being stale.
	 * @param changes that need to be saved
	 */
	function handleChangeActionTypes(changes) {
		handleUpdateMultiplePropsAtOnce(changes);
	}

	/**
	 * This handles the Continuation data part and renders the continuation data id as a string.  Should be disabled.
	 * @returns jsx element for the continuation document part of the form
	 */
	function handleContinuationId() {
		const html = (
			<div className={styles.row} data-test="field-prop-list-item" key={"continuation-data-wrapper"}>
				<ContinuationData
					fieldTemplate={values}
					continuationId={values.continuationDataId}
					continuationData={values.continuationData}
					handleChange={handleChangeContinuationData}
					handleSubmit={handleSubmit}
					online={online}
					color={color}
				/>
				<label style={{ color: color.text.secondary }} className={styles.header} htmlFor="edit_instanceToContinueOn">
					ContinuationDataId
				</label>
				<TextFieldWrapper
					onChange={handleChange}
					value={values.continuationDataId || ""}
					name="continuationDataId"
					id={fromTable ? "continuationDataId-cell" : "continuationDataId"}
					className={styles.text_input}
					type="text"
					placeholder="continuationDataId"
					colorPalette={color}
					disabled
				/>
			</div>
		);
		return html;
	}

	/**
	 * Handler for the continuation data to update the continuation data and the id at once.
	 * @param data of the changes that need to be made
	 */
	function handleChangeContinuationData(data) {
		handleUpdateMultiplePropsAtOnce(data);
	}

	/**
	 * Handle option submit
	 * @param {String} valuesData of the field
	 * @returns html
	 */
	async function handleSubmitOption(item: Option) {
		const options = JSON.parse(JSON.stringify(values.options.items));
		const finalOptions = options.map((option) => {
			if (option.id === item.id) {
				return item;
			} else {
				return option;
			}
		});

		const event = {
			target: {
				name: "options",
				value: { items: finalOptions },
			},
		};

		handleChangeAndSubmit(event);
	}

	/**
	 * sets up the new table fields in state
	 * @param {object} valuesData new table field data
	 * @param {int} index index of its position
	 */
	function handleSubmitTableField(valuesData) {
		// need to just grab the index because we are not maintaining the rowOrder in the items and cannot guarantee its position.
		const tableFields = values.tableFields.items;

		for (let i = 0; i < tableFields.length; i++) {
			const tableField = tableFields[i];
			if (tableField.id === valuesData.id) {
				tableFields[i] = valuesData;
			}
		}

		const event = {
			target: {
				name: "tableFields",
				value: { items: tableFields },
			},
		};

		handleChangeAndSubmit(event);
	}

	/**
	 * Rearranges things for the given key in values based on the provided start and end.
	 * @param key of the property in values
	 * @param start index number
	 * @param end index number
	 */
	function handleRearrangeThings(key, start, end) {
		const propToSort = values[key];
		const sorted = reorder(propToSort, start, end);
		const eventObj = {
			target: {
				name: key,
				value: sorted,
			},
		};

		handleChangeAndSubmit(eventObj);
	}

	/**
	 * Downloads the current options as a CSV with label and value columns.
	 */
	function handleDownloadOptionsCSV() {
		const optionsItems: Option[] = values.options?.items ?? [];
		if (optionsItems.length === 0) return;
		const optionsOrder: string[] = values.optionsOrder ?? [];
		const optionsMap: Record<string, Option> = {};
		for (const opt of optionsItems) {
			optionsMap[opt.id] = opt;
		}
		const orderedOptions = optionsOrder.map((id) => optionsMap[id]).filter(Boolean);
		const rows = orderedOptions.map((opt) => {
			const label = `"${(opt.label ?? "").replace(/"/g, '""')}"`;
			const value = `"${(opt.value ?? "").replace(/"/g, '""')}"`;
			return `${label},${value}`;
		});
		const csv = ["label,value", ...rows].join("\n");
		const blob = new Blob([csv], { type: "text/csv" });
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		a.download = `${values.header ?? "options"}.csv`;
		a.click();
		URL.revokeObjectURL(url);
	}

	/**
	 * This handles batch creating options from a passed in csv.
	 * For now the CSV hsa to have the label in the first column, then the value in the second column (IF NEEDED)
	 * If the value column is empty it will set the value to be the same as the label.
	 * @param blob csv file for the options to be uploaded
	 */
	function handleBatchUploadOptions(event) {
		const labelIndex = 0;
		const valueIndex = 1;

		if (event?.target?.files) {
			// setup options
			const options = values.options.items;
			const optionsOrder = values.optionsOrder;

			const blob = event.target.files[0];
			const fileReader = new FileReader();
			fileReader.readAsText(blob, "UTF-8");
			fileReader.onload = async function (e) {
				if (e.target && typeof e.target.result === "string") {
					const csvString = e.target.result;
					const results = Papa.parse(csvString);
					const csvStringifiedArray = results.data;
					const timestamp = new Date().toISOString();
					for (let i = 0; i < csvStringifiedArray.length; i++) {
						const optionCsvEle = csvStringifiedArray[i];
						const label = optionCsvEle[labelIndex];
						let value = optionCsvEle[labelIndex];
						if (optionCsvEle[valueIndex]) {
							// there is value so value will use that instead of the labelindex
							value = optionCsvEle[valueIndex];
						}
						if (label !== "") {
							const option = createNewOptionForBatch(label, value, timestamp);
							options.push(option);
							optionsOrder.push(option.id);
						}
					}
				}

				const changeData = {
					options: { items: options },
					optionsOrder: optionsOrder,
				};
				// call handleChange multiple with updated list of options and order
				handleUpdateMultiplePropsAtOnce(changeData);
			};
		}
	}

	/**
	 * Creates a new option to be returned based on the args
	 * @param {string} label for the option
	 * @param {string} value for the option, if empty it will be set to label.
	 * @returns {Option} the new option object that will be created.
	 */
	function createNewOptionForBatch(label: string, value: string, timestamp: Date) {
		// make a new option
		const newOption = {
			id: uuid(),
			fieldTemplateId: values.id,
			formTemplate: template.formTemplate,
			docId: "",
			group: template.group,
			readGroups: template.group === "FactorEarth" ? ["PERMISSION_DefaultTemplates"] : [],
			linkedId: "",
			exportActions: [],
			exportData: [],
			createdAt: timestamp,
			updatedAt: timestamp,
			label,
			value,
		};

		const option = enforceOptions(newOption);
		return option;
	}

	/**
	 * builds html for options
	 * @param {object} value of the options
	 * @returns html
	 */
	function handleOptionsProp(value) {
		const optionCheck = ["dropdown", "radio", "checkbox"];
		if (!optionCheck.includes(values.inputType)) {
			return null;
		}
		let options = [];
		let optionsMap = {};

		if (value.items?.length > 0) {
			for (let i = 0; i < value.items.length; i++) {
				const optionTemplate = value.items[i];
				const finalOption = enforceOptions(optionTemplate);
				optionsMap[optionTemplate.id] = finalOption;
			}

			for (let i = 0; i < values.optionsOrder.length; i++) {
				const optionOrderedId = values.optionsOrder[i];
				const finalOption = optionsMap[optionOrderedId];
				const isLast = template.optionsOrder.length - 1 === i;
				const html = (
					<OptionsEditor
						index={i}
						template={finalOption}
						handleSubmitOption={handleSubmitOption}
						handleRemoveOption={handleRemoveOption}
						handleRearrangeOptions={handleRearrangeThings}
						handleShowDeleteModal={handleShowDeleteModal}
						online={online}
						userDoc={userDoc}
						isLast={isLast}
						color={color}
						key={`options-editor-${finalOption.id}`}
					/>
				);
				options.push(html);
			}
		}

		const optionWrapper = (
			<div className={styles.optionsRow} key={`options-wrapper`}>
				<div className={styles.optionsOpenHeader}>Options</div>
				<div className={styles.optionsBtn_input}>
					<div data-test="options-container">
						{options.length > 0 ? options : null}
						{options.length < 1 ? <div>There are no options, click below to add one!</div> : null}
					</div>

					<div className={styles.btn_wrapper}>
						<Button
							colorPalette={color}
							style={{ background: color.utils.success }}
							className={styles.optionsBtn}
							onClick={handleNewOptionClick}
							data-test="new-option-btn">
							+ Option{fromTable ? " from Cell" : null}
						</Button>
						<Button colorPalette={color} style={{ background: color.utils.success }} className={styles.optionsBtn}>
							{/* Insert Drawer Tutorial Here for explaining the Option csv?? */}
							<label htmlFor="batch-em-good">Upload</label>
							<input
								id="batch-em-good"
								type="file"
								accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
								onChange={(e) => {
									handleBatchUploadOptions(e);
								}}
							/>
						</Button>
						<Button
							colorPalette={color}
							style={{ background: color.utils.success }}
							className={styles.optionsBtn}
							onClick={handleDownloadOptionsCSV}
							disabled={(values.options?.items ?? []).length === 0}
							data-test="download-options-csv-btn">
							Download Options CSV
						</Button>
					</div>
				</div>
			</div>
		);

		return optionWrapper;
	}

	/**
	 * craetes new option
	 */
	function handleNewOptionClick() {
		const newDateString = new Date().toISOString();
		// make a new option
		const newOption = {
			id: uuid(),
			fieldTemplateId: values.id,
			formTemplate: template.formTemplate,
			label: "",
			value: "",
			docId: "",
			group: template.group,
			readGroups: template.group === "FactorEarth" ? ["PERMISSION_DefaultTemplates"] : [],
			linkedId: "",
			exportActions: [],
			exportData: [],
			createdAt: newDateString,
			updatedAt: newDateString,
		};

		const option = enforceOptions(newOption);
		// setup options
		const options = [...values.options.items];
		options.push(option);
		// oh yeah gotta do the optionsOrder as well
		const optionsOrder = [...values.optionsOrder];
		optionsOrder.push(option.id);

		// call handleChange with updated list of options, This is separated as two calls for the moment, because it seems like there are no issues with this...
		// If there is a bug where part of an option doesnt seem to be getting updated, then this should be changed to the handle multiple props at once.
		const changeData = {
			options: { items: options },
			optionsOrder: optionsOrder,
		};

		handleUpdateMultiplePropsAtOnce(changeData);
	}

	/**
	 * Removes the option from the
	 * @param optionToRemove Option to be removed.
	 * @param index the index of the option in the optionsOrder
	 */
	function handleRemoveOption({ optionToRemove, index }: { optionToRemove: Option; index: number }) {
		const optionsOrder = [...values.optionsOrder];
		optionsOrder.splice(index, 1);
		// remove from the items
		const filteredOptions = values.options.items.filter((option) => option.id !== optionToRemove.id);

		const changeData = {
			options: { items: filteredOptions },
			optionsOrder: optionsOrder,
		};

		handleUpdateMultiplePropsAtOnce(changeData);
	}

	/**
	 * creates new table field
	 */
	function handleNewTableFieldClick() {
		// make a new tableField
		const newTableField = {
			header: "New Cell -" + Date.now(),
			id: uuid(),
			formTemplate: template.formTemplate,
			inputType: "text",
			exportType: "text",
			docId: "",
			group: template.group,
			readGroups: template.group === "FactorEarth" ? ["PERMISSION_DefaultTemplates"] : [],
			exportActions: [],
			rowOrder: [],
			tableFields: { items: [] },
			tableField: template.id,
			options: { items: [] },
			optionsOrder: [],
			action: [],
			exportData: [],
			csvKey: "",
			identityRole: "",
			createdAt: new Date().toISOString(),
			updatedAt: new Date().toISOString(),
		};

		const tableField = enforceFieldTemplateDoc(newTableField);
		// setup tableFields
		const tableFields = values.tableFields.items;
		tableFields.push(tableField);
		// oh yeah gotta do the optionsOrder as well
		const rowOrder = [...values.rowOrder];
		rowOrder.push(tableField.id);
		// the multiple update event object
		const changeData = {
			tableFields: { items: tableFields },
			rowOrder: rowOrder,
		};
		// call handleChange with updated list of options
		handleUpdateMultiplePropsAtOnce(changeData);
	}

	/**
	 * This removes the field from the tableFields and rowOrder.  The additional cleanup occurs when the template is finally being saved at the end of the session.
	 * @param id the id of the table field to be removed
	 * @param index the index of the table field in the rowOrder
	 * @returns {void} nothing, just removes the table field from state.
	 */
	function handleRemoveTableField(id: string) {
		// Remove the field template from the fields order
		// Then remove the field from the items so it isnt added back in.
		// This also makes sure the delete process does its thing
		// remove from the items
		const tableFields = values.tableFields.items.filter((doc) => doc.id !== id);
		// .filter makes a shallow copy..
		const rowOrder = [...values.rowOrder];
		const rowIndex = rowOrder.indexOf(id);
		if (rowIndex !== -1) rowOrder.splice(rowIndex, 1);

		const changeData = {
			tableFields: { items: tableFields },
			rowOrder: rowOrder,
		};
		// not sure exactly why sometimes this works and sometimes it doesnt - May need to eventually change to handleUpdateMultiplePropsAtOnce call
		handleUpdateMultiplePropsAtOnce(changeData);
	}

	/**
	 * sets up the html for table fields
	 * @param {object} value of the table fields
	 * @returns html
	 */
	function handleTableFieldsProp(value) {
		const tableWrapper = (
			<TableFieldsEditor
				template={values}
				color={color}
				online={online}
				userDoc={userDoc}
				value={value}
				handleNewTableFieldClick={handleNewTableFieldClick}
				handleRemoveTableField={handleRemoveTableField}
				handleShowDeleteModal={handleShowDeleteModal}
				handleSubmitTableField={handleSubmitTableField}
				handleRearrangeThings={handleRearrangeThings}
				key={`${template.id}-table-fields-editor`}
				setupDrawer={setupDrawer}
			/>
		);

		return values.inputType === "table" || values.inputType === "imageroll" ? tableWrapper : null;
	}

	/**
	 * Event handler for drag and drop on drag end
	 * @param {event} e event data
	 * @param {string} key of the prop
	 */
	function onDragEnd(e, key) {
		const propToSort = values[key];
		const sorted = reorder(propToSort, e.source.index, e.destination.index);
		const eventObj = {
			target: {
				name: key,
				value: sorted,
			},
		};
		handleChangeAndSubmit(eventObj);
	}

	/**
	 * Handles rendering an input that is a list of elements.
	 * @param {string[]} array of strings that need to be editted as a list
	 */
	function handleStringListInput(array, key) {
		let html;
		if (array.length > 0) {
			html = (
				<DragDropContext key={template.id + key} onDragEnd={(e) => onDragEnd(e, key)}>
					<Droppable key={template.id + key} droppableId={`${template.id}-droppable`}>
						{(provided, snapshot) => (
							<React.Fragment key={`droppable-frag-${template.id}-${key}`}>
								<div
									{...provided.droppableProps}
									ref={provided.innerRef}
									className={styles.row}
									style={{ border: "none", borderBottom: `1px dotted ${color.border.secondary}` }}>
									<label style={{ color: color.text.primary }} className={styles.headerFieldOrder} htmlFor={`${key}-list`}>
										{key}
									</label>
									<ul className={styles.field_input} id={`${key}-list`}>
										{buildStringListElements(array)}
									</ul>
								</div>
								{provided.placeholder}
							</React.Fragment>
						)}
					</Droppable>
				</DragDropContext>
			);
		}

		return html;
	}

	/**
	 * set up string list elements with drag and drop
	 * @param {Array} array of the string list elements
	 * @returns html with drag and drop setup
	 */
	function buildStringListElements(array) {
		const final = [];
		const templateToUse = template;
		array.map((id, index) => {
			const sortedOptions = templateToUse.options?.items.length > 0 ? templateToUse.options.items.find((obj) => obj.id === id) : null;
			let sortedFields = templateToUse.fieldTemplates?.items.length > 0 ? templateToUse.fieldTemplates.items.find((obj) => obj.id === id) : null;
			if (templateToUse.tableFields?.items.length > 0) {
				sortedFields = templateToUse.tableFields.items.find((obj) => obj.id === id);
			}
			const sorted = sortedOptions ? sortedOptions.label : sortedFields?.header || "New Field";

			const html = (
				<li id={id} className={styles.fieldName} key={`string-list-thing-${id}`}>
					<div
						style={{
							color: color.text.secondary,
							background: color.background.tertiary,
						}}
						className={styles.headerName}>
						{sorted}
						<Button
							colorPalette={color}
							style={{ color: color.text.secondary, background: color.background.primary }}
							className={styles.deleteButton}
							variant="destructive"
							icon={<FiTrash2 />}
						/>
					</div>
				</li>
			);
			final.push(html);
		});

		return final;
	}

	/**
	 * set up dropdon input html.
	 * @param {string} value of the selected option
	 * @param {string} key of the prop
	 * @param {array} options for the field
	 * @returns html
	 */
	function handleDropdownInput(value, key, options) {
		const optionsHtml = [];

		for (let i = 0; i < options.length; i++) {
			const option = options[i];
			const optionHtml = (
				<option id={option} key={option} value={option}>
					{option}
				</option>
			);
			optionsHtml.push(optionHtml);
		}
		const html = (
			<div
				className={styles.optionsHeader}
				data-test="field-prop-list-item"
				isfancy="true"
				name={key}
				key={`options-wrapper-for-dropdown-input-${key}`}
				style={{ borderBottom: `1px dotted ${color.border.secondary}` }}>
				<label className={styles.header} htmlFor={key} style={{ color: color.text.secondary }}>
					{propertyProxies[key] || key}
				</label>
				<select
					name={key}
					id={key}
					onChange={handleChangeInputType}
					value={values[key]}
					data-test={`${key}-dropdown-${template.id}`}
					className={styles.fieldSelect_input}
					style={{
						background: color.background.primary,
						border: `1px solid ${color.border.primary}`,
						color: color.text.primary,
					}}>
					{optionsHtml}
				</select>
			</div>
		);

		return html;
	}

	function handleChangeInputType(e) {
		const eventObj = {
			target: {
				name: e.target.name,
				value: e.target.value,
			},
		};

		handleChangeAndSubmit(eventObj);
	}

	function handlePixelMeasureInput(value, key) {
		// This is a component to handle the pixel editing and measuring feature.
		const html = (
			<MeasurePixels
				values={{
					fontSize: values.fontSize,
					font: values.font,
					value: values[key] || "",
					text: "",
				}}
				valuesKey={key}
				handleChange={handleChangeAndSubmit}
				color={color}
				fromTable={fromTable}
			/>
		);
		return html;
	}

	function handleFont(options, key, value) {
		const html = handleDropdownInput(value, key, options);
		return html;
	}

	return (
		<div className={submitClickCallback ? styles.wrapper : fromTable ? styles.editWrapper_table : styles.editWrapper}>
			<div style={{ background: color.background.primary }} className={submitClickCallback ? styles.row : styles.editSection}>
				{renderTemplate()}
			</div>
		</div>
	);
}

export default UpdateTemplateForm;
